import { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Box, chakra, Flex, Heading, Text } from '@chakra-ui/react';
import type { SiteMode } from '@denise-augusta/domain';
import { fetchSiteMode } from '@/lib/blob-stores';
import { isAuthConfigured } from '@/config/environment';

/**
 * Gates the public site behind the site access mode:
 *
 *   locked (default) — an Entra ID sign-in is required to view anything
 *   live             — the site is open; only /portal requires login
 *
 * Fails closed: if the mode can't be determined the site stays locked.
 * Admins flip the mode from the dashboard (site-content:setSiteMode).
 */
export function SiteGate({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const [mode, setMode] = useState<SiteMode | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSiteMode().then((value) => {
      if (!cancelled) setMode(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (mode === null) {
    // Brief splash while the mode loads — keeps locked content from flashing.
    return <Box minH="100vh" bg="paper.warm" />;
  }

  if (mode === 'live' || isAuthenticated) {
    return <>{children}</>;
  }

  return <LockedScreen />;
}

function LockedScreen() {
  const { instance } = useMsal();

  const signIn = () =>
    instance.loginRedirect({
      scopes: ['openid', 'profile'],
      redirectStartPage: window.location.href,
    });

  return (
    <Flex minH="100vh" align="center" justify="center" bg="brand.wash" direction="column" gap="2rem" px="1.8rem" textAlign="center">
      <Box>
        <Flex align="center" justify="center" gap="0.75rem" mb="1.2rem">
          <Box w="26px" h="1px" bg="brand.mid" />
          <Text fontSize="0.67rem" fontWeight="500" letterSpacing="0.26em" textTransform="uppercase" color="brand.mid">
            Private Preview
          </Text>
          <Box w="26px" h="1px" bg="brand.mid" />
        </Flex>
        <Heading fontFamily="heading" fontSize="clamp(2.6rem, 5vw, 4rem)" fontWeight="300" color="brand.ink" mb="1rem">
          Denise{' '}
          <Box as="em" fontStyle="italic" color="brand.mid">
            Augusta
          </Box>
        </Heading>
        <Text fontFamily="heading" fontStyle="italic" fontSize="1.1rem" color="ink.soft" maxW="440px" mx="auto" lineHeight="1.7">
          This site isn't public yet — sign in to take a look around.
        </Text>
      </Box>

      {isAuthConfigured() ? (
        <chakra.button
          onClick={signIn}
          bg="brand.mid"
          color="paper.white"
          fontFamily="body"
          fontSize="0.72rem"
          fontWeight="500"
          letterSpacing="0.15em"
          textTransform="uppercase"
          px="2.8rem"
          py="1rem"
          border="none"
          cursor="pointer"
          borderRadius="24px"
          transition="background 0.2s, transform 0.2s"
          _hover={{ bg: 'brand.ink', transform: 'translateY(-1px)' }}
        >
          Sign in with Microsoft →
        </chakra.button>
      ) : (
        <Box bg="paper.white" border="1px solid" borderColor="brand.pale" borderRadius="12px" px="1.6rem" py="1.2rem" maxW="480px">
          <Text fontSize="0.87rem" color="ink.soft" lineHeight="1.7">
            The site is locked and Azure Entra ID is not configured, so sign-in is unavailable.
            Run <code>pnpm env:load</code> (or <code>node scripts/set-site-mode.mjs live</code> in
            local dev) to proceed.
          </Text>
        </Box>
      )}
    </Flex>
  );
}
